import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Heart,
  Stethoscope,
  Baby,
  Briefcase,
  Ambulance,
  Building2,
  Moon,
  Handshake,
  Shield,
  Calendar,
  Phone,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function ProtectionGuide() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <Link to="/bio/zahirul" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">🛡 Life & Protection Cover</h1>
          <p className="text-slate-400">Thames Mortgage & Protection</p>
          <p className="text-lg text-rose-400 mt-4">Helping you protect your income, health, and family's financial security.</p>
        </motion.div>

        {/* Life Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold">❤️ Life Cover</h2>
          </div>
          <p className="text-slate-300 mb-4">Life insurance provides a lump sum to support your loved ones if you pass away.</p>
          <p className="text-slate-400 mb-2">Common uses include:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Supporting your family and dependants</li>
            <li>• Repaying a mortgage or other debts</li>
            <li>• Providing long-term financial stability</li>
          </ul>
          <p className="text-rose-400 mt-4 font-medium">Cover is tailored to your needs, term, and budget.</p>
        </motion.div>

        {/* Critical Illness Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">🩺 Critical Illness Cover (CIC)</h2>
          </div>
          <p className="text-slate-300 mb-4">Pays a tax-free lump sum if you're diagnosed with a specified serious illness, such as cancer, heart attack, or stroke.</p>
          <p className="text-slate-400 mb-2">This can help with:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Medical or care costs</li>
            <li>• Mortgage or rent payments</li>
            <li>• Time off work to focus on recovery</li>
          </ul>
        </motion.div>

        {/* Child Critical Illness */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
              <Baby className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-xl font-semibold">👶 Child Critical Illness Cover</h2>
          </div>
          <p className="text-slate-300 mb-4">Provides financial support if your child is diagnosed with a serious illness.</p>
          <p className="text-slate-400 mb-2">Designed to help with:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Time away from work</li>
            <li>• Travel or specialist care costs</li>
            <li>• Peace of mind during difficult times</li>
          </ul>
          <p className="text-pink-400 mt-4 font-medium">Often available as an add-on to adult policies.</p>
        </motion.div>

        {/* Income Protection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur border border-blue-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">💼 Income Protection (IP)</h2>
          </div>
          <p className="text-slate-300 mb-4">If illness or injury prevents you from working, income protection can pay a regular monthly income until you recover or reach retirement.</p>
          <p className="text-slate-400 mb-2">Suitable for:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Employed professionals</li>
            <li>• Self-employed individuals</li>
            <li>• Company directors</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">One of the most comprehensive forms of protection.</p>
        </motion.div>

        {/* Accident Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Ambulance className="w-5 h-5 text-orange-400" />
            </div>
            <h2 className="text-xl font-semibold">🚑 Accident & Accident-Only Cover</h2>
          </div>
          <p className="text-slate-300 mb-4">Provides a lump sum or regular payments following an accident resulting in injury, disability, or death.</p>
          <p className="text-slate-400 mb-2">Useful for:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Manual workers</li>
            <li>• Self-employed clients</li>
            <li>• Additional short-term protection</li>
          </ul>
          <p className="text-orange-400 mt-4 font-medium">Can be used alongside other policies.</p>
        </motion.div>

        {/* Private Medical Insurance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-xl font-semibold">🏥 Private Medical Insurance (PMI)</h2>
          </div>
          <p className="text-slate-300 mb-4">Gives faster access to private healthcare for diagnosis and treatment.</p>
          <p className="text-slate-400 mb-2">Benefits include:</p>
          <ul className="space-y-2 text-slate-300">
            <li>• Shorter waiting times</li>
            <li>• Choice of specialists and hospitals</li>
            <li>• Faster return to work</li>
          </ul>
          <p className="text-cyan-400 mt-4 font-medium">Cover can be individual, family, or employer-provided.</p>
        </motion.div>

        {/* Takaful */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur border border-emerald-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Moon className="w-5 h-5 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold">🕌 Sharia-Compliant Protection (Takaful Options)</h2>
          </div>
          <p className="text-slate-300">Where available, we can arrange Family Takaful solutions aligned with Islamic principles as an alternative to conventional protection.</p>
        </motion.div>

        {/* Why Protection Advice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Handshake className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold">🤝 Why Protection Advice Matters</h2>
          </div>
          <p className="text-slate-400 mb-2">We help you:</p>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Choose the right type and level of cover</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Avoid paying for unnecessary protection</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Protect income, lifestyle, and dependants</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Keep cover aligned with your mortgage and wider financial plans</li>
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8"
        >
          <a href="tel:07550199399" className="block">
            <Button className="w-full h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </Button>
          </a>
          <Link to="/book-appointment" className="block">
            <Button className="w-full h-14 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Protection Review
            </Button>
          </Link>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition text-sm">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            Thames Mortgage & Protection
          </Link>
          <p className="text-xs text-slate-500 mt-3">FCA Regulated • FRN 1045227</p>
          <p className="text-xs text-amber-500 mt-2 font-medium">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
        </motion.div>
      </div>
    </div>
  );
}
